import React, { useEffect, useState } from "react";
import { useUser } from "./UserContext";
import { BeatLoader } from "react-spinners";
import "./css/YourProducts.css";
import genres from "./genreBeat";
import { useNavigate } from "react-router-dom";
import customStyles from "./css/StylesForSelect";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import EditStemModal from "./EditStemModal";
import ConfirmModal from "./ConfirmModal";

import BeatsSection from "./yourProducts/BeatsSection";
import TutorialsSection from "./yourProducts/TutorialsSection";
import FashionSection from "./yourProducts/FashionSection";

import {
  PRODUCT_TYPES,
  createSellerCouponApi,
  deleteCouponApi,
  deleteProductDocumentApi,
  fetchSellerCouponsApi,
  fetchSellerProducts,
  getProductDocumentApi,
  updateProductDocumentApi,
} from "./yourProducts/productApi";

import {
  buildBaseUpdateData,
  getGenreString,
  getInitialCouponInputs,
  getInitialEditValues,
  getSelectedGenreOptions,
  safeJsonParse,
} from "./yourProducts/productUtils";

import {
  buildUpdatedBeatStems,
  deleteBeatFilesFromDoc,
  deleteFashionFilesFromDoc,
  deleteTutorialFilesFromDoc,
  uploadBeatAudioWithLowPreview,
  uploadExtraFashionImages,
  uploadGeneratedAutoStems,
  uploadProductImageReplacement,
} from "./yourProducts/productStorage";

const YourProducts = () => {
  const { user } = useUser();
  const navigate = useNavigate();

  const [beats, setBeats] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [fashions, setFashions] = useState([]);

  const [playingBeat, setPlayingBeat] = useState(null);
  const [audio, setAudio] = useState(new Audio());

  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editValues, setEditValues] = useState({});

  const [newImage, setNewImage] = useState(null);
  const [extraImages, setExtraImages] = useState([]);

  const [coupons, setCoupons] = useState([]);
  const [couponLoading, setCouponLoading] = useState(false);
  const [deletingCoupon, setDeletingCoupon] = useState(null);
  const [selectedCoupons, setSelectedCoupons] = useState({});
  const [couponInputs, setCouponInputs] = useState(getInitialCouponInputs());

  const [saveLoadingId, setSaveLoadingId] = useState(null);
  const [viewStems, setViewStems] = useState(null);
  const [editStems, setEditStems] = useState([{ name: "", file: null }]);

  const [selectedGenres, setSelectedGenres] = useState([]);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [imageIndexes, setImageIndexes] = useState({});

  const genreOptions = genres.map((genre) => ({
    value: genre,
    label: genre,
  }));

  const styles = customStyles();

  useEffect(() => {
    const currentAudio = new Audio();
    setAudio(currentAudio);

    return () => {
      currentAudio.pause();
    };
  }, []);

  useEffect(() => {
    return () => {
      audio.pause();
    };
  }, [audio]);

  const togglePlay = (beat) => {
    const isCurrentlyPlaying = playingBeat && playingBeat.$id === beat.$id;

    if (isCurrentlyPlaying && !audio.paused) {
      audio.pause();
      setPlayingBeat(null);
      return;
    }

    audio.src = beat.audioUrl;
    audio.play();
    setPlayingBeat(beat);
  };

  const fetchSellerCoupons = async () => {
    if (!user) return;

    try {
      const sellerCoupons = await fetchSellerCouponsApi(user.email);
      setCoupons(sellerCoupons);
    } catch (error) {
      console.error("Failed to fetch seller coupons", error);
    }
  };

  useEffect(() => {
    fetchSellerCoupons();
  }, [user]);

  useEffect(() => {
    const loadProducts = async () => {
      if (!user) return;

      try {
        setLoading(true);

        const sellerProducts = await fetchSellerProducts(user.email);

        setBeats(sellerProducts.beats);
        setTutorials(sellerProducts.tutorials);
        setFashions(sellerProducts.fashions);
      } catch (error) {
        console.error("Failed to fetch products and tutorials", error);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [user]);

  const handleSoldClick = () => {
    navigate("/your-products/sold");
  };

  const openConfirmModal = (action) => {
    setConfirmAction(() => action);
    setConfirmModalOpen(true);
  };

  const confirmDeletion = () => {
    if (confirmAction) {
      confirmAction();
    }

    setConfirmModalOpen(false);
    setConfirmAction(null);
  };

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
    setConfirmAction(null);
  };

  const handleDeleteClick = (productId, type) => {
    if (type === PRODUCT_TYPES.TUTORIAL) {
      openConfirmModal(() => handleDeleteTutorial(productId));
    } else if (type === PRODUCT_TYPES.BEAT) {
      openConfirmModal(() => handleDeleteBeat(productId));
    } else if (type === PRODUCT_TYPES.FASHION) {
      openConfirmModal(() => handleDeleteFashion(productId));
    }
  };

  const handleDeleteFashion = async (fashionId) => {
    setDeleting(fashionId);
    setLoading(true);

    try {
      const fashionDoc = await getProductDocumentApi(
        PRODUCT_TYPES.FASHION,
        fashionId,
      );

      await deleteFashionFilesFromDoc(fashionDoc);
      await deleteProductDocumentApi(PRODUCT_TYPES.FASHION, fashionId);

      setFashions((prev) =>
        prev.filter((fashion) => fashion.$id !== fashionId),
      );
    } catch (error) {
      console.error("Failed to delete fashion item:", error);
    } finally {
      setDeleting(null);
      setLoading(false);
    }
  };

  const handleDeleteBeat = async (beatId) => {
    setDeleting(beatId);
    setLoading(true);

    try {
      const beatDoc = await getProductDocumentApi(PRODUCT_TYPES.BEAT, beatId);

      await deleteBeatFilesFromDoc(beatDoc);
      await deleteProductDocumentApi(PRODUCT_TYPES.BEAT, beatId);

      setBeats((prev) => prev.filter((beat) => beat.$id !== beatId));
    } catch (error) {
      console.error("Failed to delete beat:", error);
    } finally {
      setDeleting(null);
      setLoading(false);
    }
  };

  const handleDeleteTutorial = async (tutorialId) => {
    setDeleting(tutorialId);
    setLoading(true);

    try {
      const tutorialDoc = await getProductDocumentApi(
        PRODUCT_TYPES.TUTORIAL,
        tutorialId,
      );

      await deleteTutorialFilesFromDoc(tutorialDoc);
      await deleteProductDocumentApi(PRODUCT_TYPES.TUTORIAL, tutorialId);

      setTutorials((prev) =>
        prev.filter((tutorial) => tutorial.$id !== tutorialId),
      );
    } catch (error) {
      console.error("Failed to delete tutorial:", error);
    } finally {
      setDeleting(null);
      setLoading(false);
    }
  };

  const handleEdit = (item, type) => {
    // If the same product edit window is already open, close it
    if (editing === item.$id) {
      setEditing(null);
      setEditValues({});
      setNewImage(null);
      setExtraImages([]);
      setSelectedGenres([]);
      setEditStems([{ name: "", file: null }]);
      return;
    }

    // Otherwise open this product's edit window
    setEditing(item.$id);
    setEditValues(getInitialEditValues(item, type));
    setNewImage(null);
    setExtraImages([]);

    if (type === PRODUCT_TYPES.BEAT) {
      setSelectedGenres(getSelectedGenreOptions(item, genreOptions));

      const parsedStems = safeJsonParse(item.stems, []);

      setEditStems(
        parsedStems.length
          ? parsedStems.map((stem) => ({ name: stem.name, file: null }))
          : [{ name: "", file: null }],
      );
    } else {
      setSelectedGenres([]);
      setEditStems([{ name: "", file: null }]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "cost" && value && !/^\d+$/.test(value)) {
      return;
    }

    setEditValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    setNewImage(e.target.files[0]);
  };

  const handleGenreChange = (selectedOptions) => {
    setSelectedGenres(selectedOptions || []);

    setEditValues((prev) => ({
      ...prev,
      genres: selectedOptions || [],
    }));
  };

  const handleAudioUpload = async (audioFile, item) => {
    if (!audioFile || !user) return;

    try {
      const updateData = await uploadBeatAudioWithLowPreview({
        audioFile,
        sellerEmail: user.email,
        beatName: item.beatName,
        artistName: item.artistName || "Unknown",
      });

      await updateProductDocumentApi(PRODUCT_TYPES.BEAT, item.$id, updateData);

      setBeats((prev) =>
        prev.map((beat) =>
          beat.$id === item.$id ? { ...beat, ...updateData } : beat,
        ),
      );

      console.log("Audio uploaded and updated successfully.");
    } catch (error) {
      console.error("Error uploading audio:", error);
      toast.error(error.message || "Audio upload failed");
    }
  };

  const handleSave = async (item) => {
    setSaveLoadingId(item.$id);

    const type = editValues.type;

    try {
      const updateData = buildBaseUpdateData(editValues);

      const genreString = getGenreString(editValues.genres);

      if (type === PRODUCT_TYPES.BEAT && genreString) {
        updateData.genre = genreString;
      }

      const latestItemData =
        type === PRODUCT_TYPES.BEAT
          ? beats.find((beat) => beat.$id === item.$id)
          : type === PRODUCT_TYPES.TUTORIAL
            ? tutorials.find((tutorial) => tutorial.$id === item.$id)
            : fashions.find((fashion) => fashion.$id === item.$id);

      if (newImage) {
        const imageUpdate = await uploadProductImageReplacement({
          type,
          itemData: latestItemData,
          newImage,
        });

        Object.assign(updateData, imageUpdate);
      }

      if (type === PRODUCT_TYPES.FASHION && extraImages.length > 0) {
        const extraImageUpdate = await uploadExtraFashionImages({
          fashionPath: latestItemData.fashionPath,
          extraImages,
        });

        if (extraImageUpdate.extraFashionPaths) {
          updateData.extraFashionPaths = extraImageUpdate.extraFashionPaths;
          updateData.extraFashionUrls = extraImageUpdate.extraFashionUrls;
        }
      }

      if (type === PRODUCT_TYPES.BEAT) {
        updateData.stems = await buildUpdatedBeatStems({
          item,
          beatData: latestItemData,
          editStems,
          sellerEmail: user.email,
          toast,
        });
      }

      await updateProductDocumentApi(type, item.$id, updateData);

      if (type === PRODUCT_TYPES.BEAT) {
        setBeats((prev) =>
          prev.map((beat) =>
            beat.$id === item.$id ? { ...beat, ...updateData } : beat,
          ),
        );
      } else if (type === PRODUCT_TYPES.TUTORIAL) {
        setTutorials((prev) =>
          prev.map((tutorial) =>
            tutorial.$id === item.$id
              ? { ...tutorial, ...updateData }
              : tutorial,
          ),
        );
      } else {
        setFashions((prev) =>
          prev.map((fashion) =>
            fashion.$id === item.$id ? { ...fashion, ...updateData } : fashion,
          ),
        );
      }

      setEditing(null);
      setNewImage(null);
      setExtraImages([]);
    } catch (error) {
      console.error("❌ Failed to update item", error);
      toast.error(error.message || "Failed to update item");
    } finally {
      setSaveLoadingId(null);
    }
  };

  const createCoupon = async (category) => {
    const currentInput = couponInputs?.[category] || {
      code: "",
      discount: "",
    };

    if (!currentInput.code || !currentInput.discount) {
      return;
    }

    setCouponLoading(true);

    const formattedCouponCode = currentInput.code.trim().toUpperCase();

    try {
      await createSellerCouponApi({
        category,
        couponCode: formattedCouponCode,
        discountPercent: parseInt(currentInput.discount, 10),
        sellerEmail: user.email,
      });

      setCouponInputs((prev) => ({
        ...prev,
        [category]: { code: "", discount: "" },
      }));

      fetchSellerCoupons();
    } catch (error) {
      console.error("Failed to create coupon", error);
    } finally {
      setCouponLoading(false);
    }
  };

  const deleteCoupon = async (couponId) => {
    if (!couponId) return;

    setDeletingCoupon(couponId);

    try {
      await deleteCouponApi(couponId);

      setCoupons((prev) => prev.filter((coupon) => coupon.$id !== couponId));

      setSelectedCoupons((prev) => {
        const updated = { ...prev };

        Object.keys(updated).forEach((key) => {
          if (updated[key]?.$id === couponId) {
            delete updated[key];
          }
        });

        return updated;
      });
    } catch (error) {
      console.error("Failed to delete coupon", error);
    } finally {
      setDeletingCoupon(null);
    }
  };

  const handleCouponSelect = (couponCode, category) => {
    const selectedCoupon = coupons.find(
      (coupon) => coupon.couponCode === couponCode,
    );

    if (selectedCoupon) {
      setSelectedCoupons((prev) => ({
        ...prev,
        [category]: selectedCoupon,
      }));
    }
  };
  const handleUploadAutoStems = async (generatedStems) => {
    if (!viewStems || !user) return;

    setSaveLoadingId(viewStems.$id);

    try {
      const stemsJson = await uploadGeneratedAutoStems({
        beatData: viewStems,
        generatedStems,
        sellerEmail: user.email,
        toast,
      });

      const updateData = {
        stems: stemsJson,
      };

      await updateProductDocumentApi(
        PRODUCT_TYPES.BEAT,
        viewStems.$id,
        updateData,
      );

      setBeats((prev) =>
        prev.map((beat) =>
          beat.$id === viewStems.$id ? { ...beat, ...updateData } : beat,
        ),
      );

      setViewStems((prev) => (prev ? { ...prev, ...updateData } : prev));

      toast.success("Auto-created stems saved to beat.");
    } catch (error) {
      console.error("Failed to upload auto-created stems:", error);
      toast.error(error.message || "Failed to upload auto-created stems.");
    } finally {
      setSaveLoadingId(null);
    }
  };

  return (
    <div className="your-products-container">
      <div className="your-prod-title">
        <h2>Your Products</h2>

        <button onClick={handleSoldClick} className="sold-button">
          Sold Products
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center" }}>
          <BeatLoader color="#3498db" />
        </div>
      ) : (
        <>
          <BeatsSection
            beats={beats}
            audio={audio}
            playingBeat={playingBeat}
            editing={editing}
            editValues={editValues}
            editStems={editStems}
            saveLoadingId={saveLoadingId}
            selectedGenres={selectedGenres}
            genreOptions={genreOptions}
            styles={styles}
            deleting={deleting}
            coupons={coupons}
            selectedCoupons={selectedCoupons}
            couponInputs={couponInputs}
            couponLoading={couponLoading}
            deletingCoupon={deletingCoupon}
            setCouponInputs={setCouponInputs}
            setEditStems={setEditStems}
            setViewStems={setViewStems}
            togglePlay={togglePlay}
            handleEdit={handleEdit}
            handleInputChange={handleInputChange}
            handleImageChange={handleImageChange}
            handleGenreChange={handleGenreChange}
            handleAudioUpload={handleAudioUpload}
            handleSave={handleSave}
            handleDeleteClick={handleDeleteClick}
            handleCouponSelect={handleCouponSelect}
            createCoupon={createCoupon}
            deleteCoupon={deleteCoupon}
          />

          <TutorialsSection
            tutorials={tutorials}
            editing={editing}
            editValues={editValues}
            saveLoadingId={saveLoadingId}
            deleting={deleting}
            coupons={coupons}
            selectedCoupons={selectedCoupons}
            couponInputs={couponInputs}
            couponLoading={couponLoading}
            deletingCoupon={deletingCoupon}
            setCouponInputs={setCouponInputs}
            handleEdit={handleEdit}
            handleInputChange={handleInputChange}
            handleImageChange={handleImageChange}
            handleSave={handleSave}
            handleDeleteClick={handleDeleteClick}
            handleCouponSelect={handleCouponSelect}
            createCoupon={createCoupon}
            deleteCoupon={deleteCoupon}
          />

          <FashionSection
            fashions={fashions}
            editing={editing}
            editValues={editValues}
            saveLoadingId={saveLoadingId}
            deleting={deleting}
            imageIndexes={imageIndexes}
            coupons={coupons}
            selectedCoupons={selectedCoupons}
            couponInputs={couponInputs}
            couponLoading={couponLoading}
            deletingCoupon={deletingCoupon}
            setCouponInputs={setCouponInputs}
            setImageIndexes={setImageIndexes}
            setExtraImages={setExtraImages}
            handleEdit={handleEdit}
            handleInputChange={handleInputChange}
            handleImageChange={handleImageChange}
            handleSave={handleSave}
            handleDeleteClick={handleDeleteClick}
            handleCouponSelect={handleCouponSelect}
            createCoupon={createCoupon}
            deleteCoupon={deleteCoupon}
          />
        </>
      )}

      <ToastContainer position="top-center" />

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={closeConfirmModal}
        onConfirm={confirmDeletion}
      />

      {viewStems && (
        <EditStemModal
          stems={safeJsonParse(viewStems.stems, [])}
          mainBeatFile={viewStems.audioUrl}
          beatName={viewStems.beatName}
          artistName={viewStems.artistName || "Unknown"}
          onUploadAutoStems={handleUploadAutoStems}
          onClose={() => setViewStems(null)}
        />
      )}
    </div>
  );
};

export default YourProducts;
